<template>
  <div class="cadence-picker">
    <div class="aq-form-label">UPDATE FREQUENCY</div>
    <div class="cadence-segment">
      <button
        v-for="opt in frequencyOptions"
        :key="opt.value"
        type="button"
        class="cadence-segment__btn"
        :class="{ 'cadence-segment__btn--active': value.frequency === opt.value }"
        @click="setFrequency(opt.value)"
      >
        {{ opt.label }}
      </button>
    </div>

    <div v-if="value.frequency === 'weekly'" class="cadence-detail-row">
      <div class="cadence-detail-group">
        <span class="cadence-detail-label">Day</span>
        <el-select :value="value.day" size="small" popper-class="happily-dropdown-popper" @change="updateField('day', $event)">
          <el-option v-for="d in dayOptions" :key="d.value" :label="d.label" :value="d.value" />
        </el-select>
      </div>
      <div class="cadence-detail-group">
        <span class="cadence-detail-label">Time</span>
        <el-time-picker
          :value="parsedTime"
          size="small"
          format="HH:mm"
          value-format="HH:mm"
          :clearable="false"
          @input="onTimeChange"
        />
      </div>
    </div>

    <div v-else-if="value.frequency === 'monthly'" class="cadence-detail-row">
      <div class="cadence-detail-group">
        <span class="cadence-detail-label">Day of month</span>
        <el-input-number
          :value="value.day_of_month || 1"
          :min="1"
          :max="28"
          size="small"
          controls-position="right"
          @change="updateField('day_of_month', $event)"
        />
      </div>
      <div class="cadence-detail-group">
        <span class="cadence-detail-label">Time</span>
        <el-time-picker
          :value="parsedTime"
          size="small"
          format="HH:mm"
          value-format="HH:mm"
          :clearable="false"
          @input="onTimeChange"
        />
      </div>
    </div>

    <div v-else class="cadence-detail-row">
      <div class="cadence-detail-group">
        <span class="cadence-detail-label">Time</span>
        <el-time-picker
          :value="parsedTime"
          size="small"
          format="HH:mm"
          value-format="HH:mm"
          :clearable="false"
          @input="onTimeChange"
        />
      </div>
    </div>

    <div class="cadence-fineprint">
      Update will run {{ summary }}, in your team timezone.
    </div>
  </div>
</template>

<script>
const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
}

export default {
  name: 'AutomationCadencePicker',
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  computed: {
    frequencyOptions() {
      return [
        { value: 'weekly', label: 'Weekly' },
        { value: 'daily', label: 'Daily' },
        { value: 'monthly', label: 'Monthly' },
      ]
    },
    dayOptions() {
      return Object.keys(DAY_LABELS).map(value => ({ value, label: DAY_LABELS[value] }))
    },
    parsedTime() {
      return this.value.time || '09:00'
    },
    summary() {
      if (this.value.frequency === 'daily') return `daily at ${this.parsedTime}`
      if (this.value.frequency === 'monthly') return `monthly on day ${this.value.day_of_month || 1} at ${this.parsedTime}`
      const day = DAY_LABELS[this.value.day || 'monday']
      return `weekly on ${day} at ${this.parsedTime}`
    },
  },
  methods: {
    setFrequency(freq) {
      const next = { ...this.value, frequency: freq }
      if (freq === 'weekly' && !next.day) next.day = 'monday'
      if (freq === 'monthly' && !next.day_of_month) next.day_of_month = 1
      if (!next.time) next.time = '09:00'
      if (typeof next.suppress_if_no_meaningful_change !== 'boolean') {
        next.suppress_if_no_meaningful_change = true
      }
      this.$emit('input', next)
    },
    updateField(field, val) {
      this.$emit('input', { ...this.value, [field]: val })
    },
    onTimeChange(val) {
      this.updateField('time', val || '09:00')
    },
  },
}
</script>

<style scoped>
.cadence-picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.aq-form-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a8a8a;
}

.cadence-segment {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fafbfc;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  padding: 4px;
  width: fit-content;
}

.cadence-segment__btn {
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #4a4a4a;
  background: transparent;
  border: none;
  padding: 6px 16px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.cadence-segment__btn:hover {
  color: #6c30cc;
}

.cadence-segment__btn--active {
  background: #f2ecfb;
  color: #6c30cc;
  font-weight: 700;
}

.cadence-detail-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 4px;
}

.cadence-detail-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cadence-detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #4a4a4a;
}

.cadence-fineprint {
  font-size: 11px;
  font-weight: 500;
  color: #8a8a8a;
  font-style: italic;
  margin-top: 4px;
}
</style>
